import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '@/routes/api';

// Get API base URL from centralized route configuration
const API_BASE_URL = getApiBaseUrl();

// sessionStorage flag set right before the session-expired redirect so the
// login page can show a "session expired" toast after the full-page navigation.
export const AUTH_REDIRECT_REASON_KEY = 'auth_redirect_reason';
export const AUTH_REDIRECT_REASON_SESSION_EXPIRED = 'session_expired';

// Endpoints that must never trigger the refresh-and-retry flow:
// a 401 from these means "bad credentials / bad refresh token", not "stale access token".
const REFRESH_EXEMPT_PATHS = [
  '/auth/login',
  '/auth/superadmin/login',
  '/auth/refresh',
  '/auth/register',
  '/auth/logout',
];

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});

// Bare client for the refresh call itself. It has no interceptors, so a 401
// from /auth/refresh can never recurse back into the refresh flow.
// Exported for unit tests (adapter override).
export const authRefreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Accept': 'application/json' },
});

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retried?: boolean };

function getStoredToken(): string | null {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
}

/** Persist a refreshed token everywhere login() persists the original one. */
function storeToken(token: string): void {
  sessionStorage.setItem('access_token', token);
  localStorage.setItem('access_token', token);
  apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
}

/**
 * Clear ONLY auth-related keys. Never wipe unrelated
 * sessionStorage/localStorage entries (drafts, locale, consent, ...).
 */
function clearAuthStorage(): void {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  delete apiClient.defaults.headers.common.Authorization;
}

function isRefreshExempt(url?: string): boolean {
  if (!url) return false;
  return REFRESH_EXEMPT_PATHS.some((path) => url.includes(path));
}

function setAuthorizationHeader(config: InternalAxiosRequestConfig, token: string): void {
  config.headers = config.headers ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headersAny = config.headers as any;
  // Axios v1 uses AxiosHeaders with a .set() API; plain objects also work.
  if (typeof headersAny.set === 'function') {
    headersAny.set('Authorization', `Bearer ${token}`);
  } else {
    headersAny.Authorization = `Bearer ${token}`;
  }
}

// ---------------------------------------------------------------------------
// Single-flight token refresh (JUB-76)
// ---------------------------------------------------------------------------
// When several requests hit 401 at the same moment (token just expired), only
// ONE refresh call goes out; all callers await the same promise and then retry
// with the new token. The promise slot resets in `finally` so a later expiry
// starts a fresh refresh.
let refreshPromise: Promise<string> | null = null;

async function performTokenRefresh(): Promise<string> {
  const currentToken = getStoredToken();
  if (!currentToken) {
    throw new Error('No access token available to refresh');
  }

  const response = await authRefreshClient.post('/auth/refresh', null, {
    headers: { Authorization: `Bearer ${currentToken}` },
  });

  // Success responses may be wrapped ({ message_key, data: { accessToken } })
  // and camelCased by the backend transformer; be tolerant of both shapes.
  const body = response.data ?? {};
  const payload = body.data ?? body;
  const newToken: string | undefined = payload.accessToken ?? payload.access_token;

  if (!newToken) {
    throw new Error('Token refresh response did not contain an access token');
  }

  storeToken(newToken);
  return newToken;
}

function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Hard logout: clear auth keys only, remember why and where we were, then
 * send the user to the login page with a returnTo so they can resume.
 */
function handleSessionExpired(): void {
  clearAuthStorage();

  const currentPath = window.location.pathname;
  // Only redirect to login if not already on the login page
  if (currentPath.includes('/login')) {
    return;
  }

  const locale = currentPath.match(/^\/(ro|en)/)?.[1] || 'ro';
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const isManagementPath = /^\/(ro|en)\/(management|superadmin)(\/|$)/.test(currentPath);

  // Flag read (and cleared) by LoginPage / SuperadminLoginPage to show the "session expired" toast
  // after the full-page redirect below.
  sessionStorage.setItem(AUTH_REDIRECT_REASON_KEY, AUTH_REDIRECT_REASON_SESSION_EXPIRED);

  // Note: Using window.location.href for redirect, so we can't use route constants here
  const loginBase = isManagementPath ? `/${locale}/management/login` : `/${locale}/login`;
  window.location.href = `${loginBase}?returnTo=${encodeURIComponent(returnTo)}`;
}

// Request interceptor to add JWT token and handle FormData uploads
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from sessionStorage
    const token = getStoredToken();
    if (token) {
      setAuthorizationHeader(config, token);
    }
    // For FormData (file uploads), remove the default application/json Content-Type
    // so the browser sets the correct multipart/form-data boundary automatically.
    if (config.data instanceof FormData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const headersAny = config.headers as any;
      if (typeof headersAny.delete === 'function') {
        headersAny.delete('Content-Type');
      } else {
        delete headersAny['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: transparent refresh-and-retry on 401 (JUB-76)
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    // Anything that is not a 401 on a refreshable endpoint passes through.
    if (status !== 401 || !originalRequest || isRefreshExempt(originalRequest.url)) {
      return Promise.reject(error);
    }

    // The retried request came back 401 again — refresh token no longer valid.
    if (originalRequest._retried) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    try {
      const newToken = await refreshAccessToken();
      setAuthorizationHeader(originalRequest, newToken);
      // Retry the original request with the refreshed token.
      return apiClient(originalRequest);
    } catch {
      // Refresh failed: hard logout with returnTo + session-expired message.
      handleSessionExpired();
      return Promise.reject(error);
    }
  }
);

export default apiClient;
